import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, Chip, CircularProgress, Tabs, Tab, Divider, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress, Paper,
} from '@mui/material';
import {
  AttachMoney, TrendingUp, TrendingDown,
  AccountBalance, Receipt, SavingsOutlined,
} from '@mui/icons-material';
import api from '../../axios';
import ModuleHeader from '../../components/ModuleHeader';
import LancamentosFinanceiro from './LancamentosFinanceiro';
import CategoriasFinanceiro from './CategoriasFinanceiro';
import ContasFinanceiro from './ContasFinanceiro';

const TEAL = '#00F1A7';
const GREEN = '#10B981';
const RED = '#EF4444';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const tabSx = {
  mb: 2,
  '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, textTransform: 'none' },
  '& .Mui-selected': { color: 'text.primary' },
};

function formatCurrency(value) {
  if (value == null) return 'R$ 0,00';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

export default function DashboardFinanceiro() {
  const location = useLocation();
  const tabPaths = ['/financeiro', '/financeiro/lancamentos', '/financeiro/categorias', '/financeiro/contas'];
  const [tabIndex, setTabIndex] = useState(() => Math.max(0, tabPaths.indexOf(location.pathname)));

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [dashboard, setDashboard] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);
  const [balancete, setBalancete] = useState([]);

  const anoAtual = new Date().getFullYear();

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const [dashRes, lancRes, balRes] = await Promise.all([
        api.get('/financeiro/dashboard'),
        api.get('/financeiro/lancamentos', { params: { limit: 10 } }),
        api.get('/financeiro/balancete', { params: { ano: anoAtual } }),
      ]);
      setDashboard(dashRes.data);
      setLancamentos(lancRes.data?.lancamentos || []);
      setBalancete(balRes.data?.meses || []);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const cards = [
    {
      label: 'Saldo Atual',
      value: dashboard?.saldo_atual,
      icon: <AccountBalance />,
      color: TEAL,
    },
    {
      label: 'Receitas do Mês',
      value: dashboard?.receitas_mes,
      icon: <TrendingUp />,
      color: GREEN,
    },
    {
      label: 'Despesas do Mês',
      value: dashboard?.despesas_mes,
      icon: <TrendingDown />,
      color: RED,
    },
    {
      label: 'Resultado',
      value: dashboard?.resultado,
      icon: <SavingsOutlined />,
      color: dashboard?.resultado >= 0 ? GREEN : RED,
    },
  ];

  const receitasCategorias = dashboard?.receitas_por_categoria || [];
  const despesasCategorias = dashboard?.despesas_por_categoria || [];
  const maxReceita = receitasCategorias.length > 0 ? Math.max(...receitasCategorias.map(c => c.valor || 0)) : 1;
  const maxDespesa = despesasCategorias.length > 0 ? Math.max(...despesasCategorias.map(c => c.valor || 0)) : 1;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Financeiro" title="Financeiro" />

      <Tabs
        value={tabIndex}
        onChange={(_, v) => { setTabIndex(v); window.history.replaceState(null, '', tabPaths[v]); }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
        sx={tabSx}
      >
        <Tab label="Dashboard" disableRipple={false} />
        <Tab label="Lançamentos" disableRipple={false} />
        <Tab label="Categorias" disableRipple={false} />
        <Tab label="Contas" disableRipple={false} />
      </Tabs>

      {tabIndex === 1 && <LancamentosFinanceiro inTab />}
      {tabIndex === 2 && <CategoriasFinanceiro inTab />}
      {tabIndex === 3 && <ContasFinanceiro inTab />}

      {tabIndex === 0 && (
      <>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {cards.map((card, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {card.label}
                      </Typography>
                      <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: card.color, fontSize: '2rem', whiteSpace: 'nowrap' }}>
                      {formatCurrency(card.value)}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp sx={{ color: GREEN }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Receitas por Categoria
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {receitasCategorias.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhuma receita no período.
                    </Typography>
                  ) : (
                    receitasCategorias.map((item, idx) => {
                      const pct = maxReceita > 0 ? (item.valor / maxReceita) * 100 : 0;
                      return (
                        <Box key={idx} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.categoria}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: GREEN }}>
                              {formatCurrency(item.valor)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(16,185,129,0.12)',
                              '& .MuiLinearProgress-bar': { bgcolor: GREEN, borderRadius: 3 },
                            }}
                          />
                        </Box>
                      );
                    })
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingDown sx={{ color: RED }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Despesas por Categoria
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {despesasCategorias.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhuma despesa no período.
                    </Typography>
                  ) : (
                    despesasCategorias.map((item, idx) => {
                      const pct = maxDespesa > 0 ? (item.valor / maxDespesa) * 100 : 0;
                      return (
                        <Box key={idx} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.categoria}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: RED }}>
                              {formatCurrency(item.valor)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(239,68,68,0.12)',
                              '& .MuiLinearProgress-bar': { bgcolor: RED, borderRadius: 3 },
                            }}
                          />
                        </Box>
                      );
                    })
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Receipt sx={{ color: TEAL }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Últimos Lançamentos
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Data</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Descrição</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Categoria</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Valor</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }} align="center">Tipo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lancamentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum lançamento encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lancamentos.map((lanc) => (
                        <TableRow key={lanc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(lanc.data)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{lanc.descricao}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{lanc.categoria_nome || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: lanc.tipo === 'receita' ? GREEN : RED }}
                            >
                              {formatCurrency(lanc.valor)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={lanc.tipo === 'receita' ? 'Receita' : 'Despesa'}
                              size="small"
                              sx={{
                                bgcolor: lanc.tipo === 'receita' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                                color: lanc.tipo === 'receita' ? GREEN : RED,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoney sx={{ color: TEAL }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Balancete Rápido — {anoAtual}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Mês</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Receitas</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Despesas</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>Resultado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balancete.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum dado de balancete disponível.
                        </TableCell>
                      </TableRow>
                    ) : (
                      balancete.map((item, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {MESES[(item.mes || idx) - 1] || item.mes}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: GREEN }}>
                              {formatCurrency(item.receitas)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: RED }}>
                              {formatCurrency(item.despesas)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: (item.resultado || 0) >= 0 ? GREEN : RED }}
                            >
                              {formatCurrency(item.resultado)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </>
      )}
    </Container>
  );
}
